import React, { FC, useCallback, useMemo, useState } from 'react';
import { Steps, Button, Row, message, Col, Card, Checkbox, Dropdown, Menu, Popover } from 'antd';
import { UndoOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
	appendWorkOrderNotesForBuyer,
	appendWorkOrderNotesForSupplier,
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';
import {
	compareStrings,
	getCurrency,
	isInvoiceTotalGreaterThanNTE,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import {
	approveInvoiceForBuyer,
	approveInvoiceForSupplier,
	disputeInvoiceForBuyer,
	disputeInvoiceForSupplier,
	markInvoiceDraftForSupplier,
	markInvoicePaidForBuyer,
	markInvoicePaidForSupplier,
	markInvoicePendingForBuyer,
	markInvoicePendingForSupplier,
	markInvoiceProcessingForBuyer,
	markInvoiceProcessingForSupplier,
	publishInvoiceForSupplier,
	syncInvoiceApprovalHierarchyForBuyer,
	syncInvoiceApprovalHierarchyForSupplier,
} from '../../thunks/invoices_thunks';
import CapexSwitch from '../capex_switch/CapexSwitch';
import ApprovalHierarchyDisplayInvoice from '../approval_hierarchy_display_invoice/ApprovalHierarchyDisplayInvoice';
import DeclineInvoiceForm from '../decline_invoice_form/DeclineInvoiceForm';
import moment from 'moment';
import { UserName } from '../name_component/user/UserNameComponent';
import { Form } from '@ant-design/compatible';
import { ROLE_TYPES } from '../../utils/DataConstants';
import MoveBackToPendingButton from '../invoices_detail_page/MoveBackToPendingButton';
import {
	INVOICE_STATUSES,
	showBackToPendingForPaidandProcessing,
} from '../../constants/InvoiceStatuses';
import { isBuyerOrInternalTech } from '../../utils/AuthUtils';

import SendReminderButton from './SendReminderButton';
import { getContractorWO, getSanitisedWorkOrder } from '../../utils/WorkOrderUtils';
import { getInvoiceMissingProperties } from '../invoice_utilities/InvoiceUtilities';
import ViewPastRevisionsButton from '../view_past_revisions_button/ViewPastRevisionsButton';

const INVOICE_ACTIONS = {
	APPROVE: 'approve',
	PAID: 'paid',
	PROCESSING: 'processing',
	PENDING: 'pending',
	DISPUTE: 'dispute',
	DRAFT: 'draft',
};
const InvoiceActions: FC<any> = ({
	invoice,
	currentUser,
	approveInvoice,
	disputeInvoice,
	markInvoicePaid,
	markInvoiceProcessing,
	markInvoicePending,
	markInvoiceDraft,
	publishInvoice,
	appendWorkOrderNotes,
	companyConfig,
	refreshInvoice,
	style,
	userType,
	history,
	invoicesCreating,
	updateInvoice,
	syncInvoice,
	isClientView = true,
	setAddingMarkup,
	setEditingInvoice,
	setCopyInvoice,
	otherInvoiceLoading,
	otherInvoice,
}): React.ReactElement => {
	const hasCreatedInvoiceToMyClient = useMemo(
		() => otherInvoice && !!otherInvoice.id,
		[otherInvoice]
	);
	const isOtherInvoiceDraftStatus = compareStrings(
		nullSafeGetOrElse('status', otherInvoice, INVOICE_STATUSES.draft),
		INVOICE_STATUSES.draft
	);
	const isInternalTech = nullSafeGetOrElse('facility.status', currentUser, '') === 'private';
	const isThirdParty = userType === 'supplier' && !isInternalTech;
	const workOrder = getSanitisedWorkOrder(invoice.workOrder, isClientView);
	const hasContractorId = useMemo(() => nullSafeGet('contractorWorkOrderId', invoice), [invoice]);
	const statuses = [
		...(isThirdParty && isClientView ? ['draft'] : []),
		'pending',
		'approved',
		'processing',
		'paid',
	];
	const getInvoiceIndex = useCallback((status) => statuses.indexOf(status), []);
	const [currentInvoiceAction, setCurrentInvoiceAction] = useState(null);
	const [showDeclineModal, setShowDeclineModal] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);
	const [declining, setDeclining] = useState(false);
	const isLoading = useCallback(
		(action) => currentInvoiceAction === action,
		[currentInvoiceAction]
	);
	const SpendCategoryDetails = useMemo(() => {
		// CapexSwitch is work-order-backed throughout: it checks the hierarchy with
		// workOrder.id and its update reads workOrder.isCapex, so on a project invoice the
		// control renders but can never succeed. Hidden rather than left to fail.
		if (!nullSafeGet('workOrderId', invoice)) {
			return null;
		}
		return <CapexSwitch companyConfig={companyConfig} refresh={refreshInvoice} quote={invoice} />;
	}, [invoice, companyConfig, refreshInvoice]);

	const handlePaidInvoice = useCallback(() => {
		setCurrentInvoiceAction(INVOICE_ACTIONS.PAID);
		markInvoicePaid(invoice)
			.then(() => {
				message.success(`Invoice paid.`);
			})
			.finally(() => setCurrentInvoiceAction(null));
	}, [invoice, markInvoicePaid]);

	const handleDraftInvoice = useCallback(() => {
		setCurrentInvoiceAction(INVOICE_ACTIONS.DRAFT);
		markInvoiceDraft(invoice)
			.then(() => {
				message.success(`Invoice moved back to draft.`);
			})
			.finally(() => setCurrentInvoiceAction(null));
	}, [invoice, markInvoiceDraft]);
	const handlePublishInvoice = () => {
		setCurrentInvoiceAction(INVOICE_ACTIONS.PENDING);
		publishInvoice(invoice)
			.then(() => {
				message.success(`Invoice published.`);
			})
			.finally(() => setCurrentInvoiceAction(null));
	};
	const handleDisputeInvoice = useCallback(
		(values) => {
			setCurrentInvoiceAction(INVOICE_ACTIONS.DISPUTE);
			const note = {
				text: 'Invoice Disputed: '.concat(values.note),
				noteAddedBy: nullSafeGet('email', currentUser),
				noteAddedAt: moment.utc(),
			};
			const disputeNote = {
				text: values.note,
				noteAddedBy: nullSafeGet('email', currentUser),
				noteAddedAt: moment.utc(),
			};
			// Project invoices have no work order to note against.
			const workOrderNoteDetails = {
				id: nullSafeGet('workOrder.id', invoice),
				locationId: nullSafeGet('workOrder.locationId', invoice),
				note,
			};
			setDeclining(true);
			disputeInvoice(
				{ id: invoice.id, contractorWorkOrderId: invoice.contractorWorkOrderId },
				workOrderNoteDetails,
				isClientView
			)
				.then(() => {
					// A project invoice has no work order to note against, and the dispute has
					// already been saved by this point — so the note is skipped rather than
					// posted with an undefined id, whose failure would swallow the success
					// message, leave the modal open and skip the refresh.
					const workOrderNote = nullSafeGet('workOrder.id', invoice)
						? appendWorkOrderNotes(workOrderNoteDetails)
						: Promise.resolve();
					return workOrderNote.then(() => {
						message.success(`Invoice disputed.`);
						setShowDeclineModal(false);
						refreshInvoice();
					});
				})
				.catch((err) => message.error(err))
				.finally(() => {
					setCurrentInvoiceAction(null);
					setDeclining(false);
				});
		},
		[currentUser, invoice, disputeInvoice, appendWorkOrderNotes, refreshInvoice]
	);

	const lastDisputeNote = invoice.disputeNotes && invoice.disputeNotes.slice(-1)[0];
	const disputeNote = lastDisputeNote ? (
		<Col className={'py-4'}>
			<div>
				<span style={{ fontSize: 16 }}>
					Disputed by{' '}
					<UserName mode={'inline'} contact={nullSafeGet(`noteAddedByContact`, lastDisputeNote)} />{' '}
					{moment(nullSafeGet(`noteAddedAt`, lastDisputeNote)).fromNow()}
				</span>
			</div>
			<div
				style={{
					display: 'inline-flex',
					background: '#fafafa',
					color: 'rgba(0, 0, 0, 0.65)',
					borderRadius: '30px',
					padding: '8px 16px',
				}}
			>
				<span
					style={{
						fontSize: 16,
					}}
				>
					{nullSafeGet(`text`, lastDisputeNote)}
				</span>
			</div>
		</Col>
	) : null;

	const currency = getCurrency({ supplier: nullSafeGet('supplierFacility', workOrder) });
	const nte = nullSafeGet('nte', workOrder);
	const invoiceId = nullSafeGet('id', invoice);
	const totalIsGreaterThanNTE = isInvoiceTotalGreaterThanNTE(invoice, workOrder);

	const [autoPublishOnApproval, setAutoPublishOnApproval] = useState(invoice.autoPublishOnApproval);
	const togglePublishOnApproval = (e) => {
		setAutoPublishOnApproval(!autoPublishOnApproval);
		setButtonLoading(true);
		updateInvoice(
			{ ...invoice, autoPublishOnApproval: !invoice.autoPublishOnApproval },
			hasContractorId
		).then(() => {
			setButtonLoading(false);
		});
	};

	//NTE issue alert
	const nteIssueAlert =
		totalIsGreaterThanNTE && (invoice.status === 'draft' || invoice.status === 'pending') ? (
			<div className="rowSpacing">
				<Card
					style={{ border: '1px solid #8ed7eb' }}
					bodyStyle={{
						padding: '16px 24px',
						background: '#ebfbff',
					}}
				>
					<div>
						<h5 style={{ marginBottom: '4px' }}>Total is greater than NTE Amount</h5>
						<div>
							<p
								style={{
									fontSize: 16,
									marginBottom: '0.5em',
								}}
							>
								{invoice.status === 'draft'
									? `You can't submit an Invoice for more than the not-to-exceed amount ${currency.format(
											nte
									  )} for this work order. Please submit a Quote to increase not-to-exceed amount or edit this invoice.`
									: `${nullSafeGet(
											'workOrder.buyerCompany.displayName',
											invoice
									  )} can't approve this invoice because it exceeds the not-to-exceed amount of ${currency.format(
											nte
									  )} for this work order. Please submit a Quote to increase not-to-exceed amount or edit this invoice.`}
							</p>
						</div>
						<Button
							type="primary"
							key="approve"
							onClick={() => history.push(`/supplier/invoices/edit/${invoiceId}`)}
						>
							Edit Invoice
						</Button>
					</div>
				</Card>
			</div>
		) : null;
	const { missingAProperty } = getInvoiceMissingProperties(invoice);
	const publishInvoiceAlert =
		!totalIsGreaterThanNTE && invoice.status === 'draft' && !missingAProperty ? (
			// A project invoice has no work order to wait on, so it is always publishable.
			nullSafeGet('workOrder.displayStatus', invoice) === 'Completed' ||
			!nullSafeGet('workOrderId', invoice) ? (
				<div className="rowSpacing">
					<Card
						style={{ border: '1px solid #8ed7eb' }}
						bodyStyle={{
							padding: '16px 24px',
							background: '#ebfbff',
						}}
					>
						<div>
							<h5 style={{ marginBottom: '4px' }}>Publish invoice?</h5>
							<div>
								<p
									style={{
										fontSize: 16,
										marginBottom: '0.5em',
									}}
								>
									This invoice is still a draft and isn't visible to the customer yet.
								</p>
								<Button
									type="primary"
									key="approve"
									loading={isLoading(INVOICE_ACTIONS.PENDING)}
									onClick={handlePublishInvoice}
								>
									Publish invoice
								</Button>
							</div>
						</div>
					</Card>
				</div>
			) : (
				<div className="rowSpacing">
					<Card
						style={{ border: '1px solid #8ed7eb' }}
						bodyStyle={{
							padding: '16px 24px',
							background: '#ebfbff',
						}}
					>
						<div>
							<h5 style={{ marginBottom: '4px' }}>
								Can't publish invoice yet. Waiting on work order completion.
							</h5>
							<div>
								<p
									style={{
										fontSize: 16,
										marginBottom: '0.5em',
									}}
								>
									This invoice is still a draft and isn't visible to the customer yet.
								</p>
								<div className="flex flex-row justify-between">
									<div className="flex flex-grow flex-col flex-nowrap">
										<div className="flex flex-row" style={{ gap: 8, alignItems: 'center' }}>
											<Button
												type="primary"
												key="approve"
												disabled={true}
												loading={isLoading(INVOICE_ACTIONS.PENDING)}
												onClick={handlePublishInvoice}
											>
												Publish invoice
											</Button>
											<SendReminderButton workOrder={nullSafeGet('workOrder', invoice)} />
											<Checkbox
												style={{ marginLeft: 12 }}
												checked={autoPublishOnApproval}
												onClick={togglePublishOnApproval}
												disabled={buttonLoading}
											/>
											<label>Auto publish invoice once work is approved</label>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Card>
				</div>
			)
		) : null;
	const showBackToPending = isBuyerOrInternalTech(currentUser)
		? showBackToPendingForPaidandProcessing(invoice)
		: false;
	const isInvoiceApprovedAndMore = [
		INVOICE_STATUSES.paid,
		INVOICE_STATUSES.processing,
		INVOICE_STATUSES.approved,
	].some((s) => compareStrings(s, invoice.status));

	const thirdPartySupplierButtons =
		isThirdParty && isClientView ? (
			<div className="flex flex-row items-end">
				{compareStrings(invoice.status, 'pending') ? (
					<div className="flex flex-row justify-between">
						<div className="flex flex-grow flex-col flex-nowrap">
							<div className="flex flex-row items-baseline">
								<h5>This invoice is pending approval.</h5>
								<ViewPastRevisionsButton record={invoice} recordType={'invoice'} />
							</div>
							<div className="flex flex-row" style={{ gap: 20, marginBottom: 20 }}>
								<Button
									key="draft"
									type="default"
									icon={<UndoOutlined />}
									loading={isLoading(INVOICE_ACTIONS.DRAFT)}
									onClick={handleDraftInvoice}
								>
									Move Back To Draft
								</Button>
							</div>
						</div>
					</div>
				) : compareStrings(invoice.status, 'disputed') ? (
					<div className="flex flex-row justify-between">
						<div className="flex flex-grow flex-col flex-nowrap">
							<h5>This invoice has been disputed.</h5>
						</div>
					</div>
				) : null}
				{isInvoiceApprovedAndMore ? (
					<div>
						<div className="flex flex-row items-baseline">
							<h5>Has this invoice been paid?</h5>
							<ViewPastRevisionsButton record={invoice} recordType={'invoice'} />
						</div>
						<div className="flex flex-row" style={{ marginBottom: 40 }}>
							{compareStrings(nullSafeGet('status', invoice), INVOICE_STATUSES.paid) ? null : (
								<Button
									type="primary"
									key="approve"
									onClick={handlePaidInvoice}
									loading={buttonLoading}
								>
									Yes, it was paid
								</Button>
							)}
							{showBackToPending ? (
								<div style={{ marginLeft: 16 }}>
									<MoveBackToPendingButton invoice={invoice} />
								</div>
							) : null}
						</div>
					</div>
				) : null}
			</div>
		) : null;

	const canSeeApprovals = (isThirdParty && !isClientView) || isBuyerOrInternalTech(currentUser);

	return (
		<div>
			<div style={{ marginTop: 24, marginBottom: 12 }}>
				<Steps current={getInvoiceIndex(invoice.status)}>
					{isThirdParty && isClientView ? <Steps.Step title="Draft" /> : null}
					<Steps.Step title="Pending" />
					<Steps.Step title="Approved" />
					<Steps.Step title="Processing" />
					<Steps.Step title="Paid" />
				</Steps>
				<div style={{ marginTop: 12 }}>
					<h5 style={{ marginBottom: 0 }}>Invoice {invoice.id}</h5>
					{invoice.status === 'disputed' ? disputeNote : null}
				</div>
			</div>
			{isThirdParty ? (
				isClientView ? (
					<div>
						{nteIssueAlert}
						{publishInvoiceAlert}
						{thirdPartySupplierButtons}
					</div>
				) : null
			) : (
				<div>
					<DeclineInvoiceForm
						visible={showDeclineModal}
						onCancel={() => setShowDeclineModal(false)}
						onSubmit={handleDisputeInvoice}
						declining={declining}
					/>
				</div>
			)}
			{canSeeApprovals ? (
				<div
					style={{
						display: 'flex',
						position: 'relative',
						height: 'fit-content',
						flexDirection: 'row',
					}}
				>
					<div
						style={{
							position: 'relative',
							flexDirection: 'column',
							width: '100%',
						}}
					>
						<Row className="w-full justify-between">
							<ApprovalHierarchyDisplayInvoice
								workOrder={invoice.workOrder}
								invoice={invoice}
								refresh={refreshInvoice}
								currentUser={currentUser}
								approveInvoice={approveInvoice}
								declineInvoice={disputeInvoice}
								markInvoicePending={markInvoicePending}
								markInvoiceProcessing={markInvoiceProcessing}
								markInvoicePaid={markInvoicePaid}
								appendWorkOrderNotes={appendWorkOrderNotes}
								style={style}
								companyConfig={companyConfig}
								userType={userType}
								syncInvoice={syncInvoice}
							/>
							<div>
								{isBuyerOrInternalTech(currentUser) ? (
									SpendCategoryDetails
								) : (
									<Popover
										content={
											isOtherInvoiceDraftStatus
												? hasCreatedInvoiceToMyClient
													? 'Warning: This will update your existing invoice.'
													: null
												: 'Your invoice must be moved back to draft status to edit it.'
										}
										placement={'left'}
									>
										<div className="flex flex-row justify-end">
											<Dropdown
												disabled={!isOtherInvoiceDraftStatus || otherInvoiceLoading}
												overlay={
													<Menu>
														<Menu.Item
															onClick={() => {
																setAddingMarkup(true);
																setCopyInvoice(invoice);
															}}
														>
															Add Markup
														</Menu.Item>
														<Menu.Item
															onClick={() => {
																setEditingInvoice(true);
																setCopyInvoice(invoice);
															}}
														>
															Edit and Copy
														</Menu.Item>
													</Menu>
												}
											>
												<Button style={{ marginRight: 12 }} onClick={(e) => e.preventDefault()}>
													Copy Invoice
												</Button>
											</Dropdown>
										</div>
									</Popover>
								)}
							</div>
						</Row>
					</div>
				</div>
			) : null}
		</div>
	);
};
const mapStateToProps = (state, ownProps) => ({
	match: ownProps.match,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
	history: ownProps.history,
	invoicesCreating: state.invoices.creating,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	approveInvoice: (invoice, isClientView) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? approveInvoiceForSupplier(invoice, isClientView)
				: approveInvoiceForBuyer(invoice)
		),
	markInvoicePaid: ({ id, contractorWorkOrderId }, isClientView) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? markInvoicePaidForSupplier({ id, contractorWorkOrderId }, isClientView)
				: markInvoicePaidForBuyer({ id, contractorWorkOrderId })
		),
	markInvoiceProcessing: ({ id, contractorWorkOrderId }, isClientView) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? markInvoiceProcessingForSupplier({ id, contractorWorkOrderId }, isClientView)
				: markInvoiceProcessingForBuyer({ id, contractorWorkOrderId })
		),
	markInvoicePending: ({ id, contractorWorkOrderId }, isClientView) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? markInvoicePendingForSupplier({ id, contractorWorkOrderId }, isClientView)
				: markInvoicePendingForBuyer({ id, contractorWorkOrderId })
		),
	markInvoiceDraft: ({ id, contractorWorkOrderId }, isClientView) =>
		dispatch(markInvoiceDraftForSupplier({ id, contractorWorkOrderId }, isClientView)),
	disputeInvoice: ({ id, contractorWorkOrderId }, disputeNote, isClientView) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? disputeInvoiceForSupplier({ contractorWorkOrderId, id, disputeNote }, isClientView)
				: disputeInvoiceForBuyer({ id, disputeNote })
		),
	updateWorkOrderPartially: (id, workOrder) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrdersRestCrudThunksForSupplier.updatePartially(id, workOrder)
				: workOrdersRestCrudThunksForBuyer.updatePartially(id, workOrder)
		),
	appendWorkOrderNotes: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? appendWorkOrderNotesForSupplier(entity)
				: appendWorkOrderNotesForBuyer(entity)
		),
	publishInvoice: ({ id, contractorWorkOrderId }, isClientView) =>
		dispatch(publishInvoiceForSupplier({ id, contractorWorkOrderId }, isClientView)),
	syncInvoice: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? syncInvoiceApprovalHierarchyForSupplier(entity)
				: syncInvoiceApprovalHierarchyForBuyer(entity)
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(Form.create()(InvoiceActions))
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
